import { withBase } from "@/lib/base-path";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { languagesWithSavedVoices } from "@/lib/nova-sonic-languages";
import { foldersWithDocuments } from "@shared/knowledge-base-path";
import { AudioLines, FileText, Loader2, Mail, Pause, Phone, Play, RefreshCw, X } from "lucide-react";

export interface NovaSonicAgentSummary {
  id: string;
  name: string;
  flowId?: string | null;
  language?: string | null;
  novaSonicVoice?: string | null;
  systemPrompt?: string | null;
  maxDurationSeconds?: number | null;
  phoneNumberId?: string | null;
  // The settings an ElevenLabs agent has too
  knowledgeBaseFolderPath?: string | null;
  transferPhoneNumber?: string | null;
  meetingBookingEnabled?: boolean | null;
  meetingHostName?: string | null;
  meetingHostEmail?: string | null;
  meetingBookingUrl?: string | null;
  salesAgentEmails?: string[] | null;
  timezone?: string | null;
  /** Agent settings that have no column of their own; announceAi is false when the agent does not open by saying it is an AI. */
  config?: { announceAi?: boolean } | null;
}

interface NovaSonicAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set to edit an existing Nova Sonic agent. */
  agent?: NovaSonicAgentSummary | null;
}

interface NovaVoice {
  voice_id: string;
  name: string;
  description: string;
  locale: string;
  polyglot: boolean;
  labels: { language: string; gender: string; accent: string };
  preview_url: string;
  /** Already added to the account's Voice Library. */
  saved?: boolean;
}

const NO_FLOW = "__none__";
const LINK_DEFAULT = "__default__";
const LINK_CUSTOM = "__custom__";

interface NovaLanguage {
  code: string;
  name: string;
  voiceCount: number;
  /** Voices of this language in My Voices. */
  savedCount?: number;
  recommended: boolean;
}

interface NovaLanguages {
  /** True when the account's billing country is India. */
  india: boolean;
  defaultLanguage: string;
  languages: NovaLanguage[];
}

/**
 * Create or edit an agent that speaks with Amazon Nova Sonic over one of your phone numbers.
 * It runs a flow from the flow builder, or free-form instructions when no flow is chosen.
 * Nova Sonic covers Hindi and English for Indian callers; other Indian languages are not supported.
 */
export default function NovaSonicAgentDialog({ open, onOpenChange, agent }: NovaSonicAgentDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const editing = !!agent;

  const [name, setName] = useState("");
  const [flowId, setFlowId] = useState(NO_FLOW);
  const [instructions, setInstructions] = useState("");
  const [language, setLanguage] = useState("");
  const [voice, setVoice] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [kbFolder, setKbFolder] = useState("");
  const [transferNumber, setTransferNumber] = useState("");
  const [bookingEnabled, setBookingEnabled] = useState(false);
  const [announceAi, setAnnounceAi] = useState(true);
  const [attendees, setAttendees] = useState<string[]>([""]);
  const [bookingUrl, setBookingUrl] = useState("");
  const [customLink, setCustomLink] = useState(false);
  const [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(agent?.name ?? "");
    setFlowId(agent?.flowId || NO_FLOW);
    setInstructions(agent?.systemPrompt ?? "");
    setLanguage(agent?.language ?? ""); // empty until the account's default language arrives
    setVoice(agent?.novaSonicVoice ?? "");
    setPhoneNumberId(agent?.phoneNumberId ?? "");
    setKbFolder(agent?.knowledgeBaseFolderPath ?? "");
    setTransferNumber(agent?.transferPhoneNumber ?? "");
    setBookingEnabled(agent?.meetingBookingEnabled ?? false);
    setAnnounceAi(agent?.config?.announceAi !== false);
    setAttendees(agent?.salesAgentEmails?.length ? agent.salesAgentEmails : [""]);
    setBookingUrl(agent?.meetingBookingUrl ?? "");
    setCustomLink(false);
  }, [open, agent]);

  useEffect(() => {
    if (!open) stopPreview();
    return stopPreview;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { data: flows = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/flow-automation/flows"],
    enabled: open,
    staleTime: 0,
  });

  // The booking links of the Cal.com account connected in settings; the agent picks one
  const { data: calcom } = useQuery<{ connected: boolean; username?: string | null; eventTypes: Array<{ id: number; title: string; slug: string; duration: number; link: string }> }>({
    queryKey: ["/api/agents/nova-sonic/calcom-event-types"],
    queryFn: async () => (await apiRequest("GET", "/api/agents/nova-sonic/calcom-event-types")).json(),
    enabled: open && bookingEnabled,
    staleTime: 60_000,
  });
  const linkOptions = calcom?.eventTypes ?? [];
  const pickedLink = !bookingUrl.trim() && !customLink ? LINK_DEFAULT : linkOptions.find((t) => t.link === bookingUrl.trim())?.link ?? LINK_CUSTOM;
  const showLinkInput = linkOptions.length === 0 || pickedLink === LINK_CUSTOM;

  // The account's numbers (Plivo and Twilio) that no agent is using (an agent being edited also sees its own number)
  const { data: plivoNumbers = [], isLoading: numbersLoading } = useQuery<Array<{ id: string; phoneNumber: string; friendlyName?: string | null; provider?: string }>>({
    queryKey: ["/api/agents/nova-sonic/numbers", agent?.id ?? "new"],
    queryFn: async () => {
      const query = agent?.id ? `?agentId=${encodeURIComponent(agent.id)}` : "";
      return (await apiRequest("GET", `/api/agents/nova-sonic/numbers${query}`)).json();
    },
    enabled: open,
    staleTime: 0,
  });

  // Languages are ordered by the account's billing country: Indian accounts see Hindi and Indian English
  // first; everyone else sees English (US, UK, Australia) first and no Hindi or Indian English.
  const { data: languageInfo } = useQuery<NovaLanguages>({
    queryKey: ["/api/agents/nova-sonic/languages"],
    enabled: open,
    staleTime: 60_000,
  });
  // Only languages with a voice in My Voices (and the agent's own, so an older agent keeps its language)
  const languages = languagesWithSavedVoices(languageInfo?.languages ?? [], [agent?.language, language]);

  useEffect(() => {
    if (open && !language && languageInfo) setLanguage(languageInfo.defaultLanguage);
  }, [open, language, languageInfo]);

  const { data: catalog = [], isLoading: loadingVoices, refetch: refetchVoices, isFetching: fetchingVoices } = useQuery<NovaVoice[]>({
    queryKey: ["/api/agents/nova-sonic/voices", language],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/agents/nova-sonic/voices?language=${encodeURIComponent(language)}`);
      return res.json();
    },
    enabled: open && !!language,
    staleTime: 0,
  });
  // Only the voices in My Voices for this language (the Voice Library page adds them). An agent that already
  // uses a voice keeps it in the list, so opening an older agent never blanks its voice.
  const voices = useMemo(() => catalog.filter((v) => v.saved || v.voice_id === agent?.novaSonicVoice), [catalog, agent?.novaSonicVoice]);

  // Knowledge base folders and the calendar host, the same sources the ElevenLabs agent form uses
  const { data: allKbFolders = [], isLoading: foldersLoading } = useQuery<any[]>({
    queryKey: ["/api/rag-knowledge/folders"],
    queryFn: async () => (await apiRequest("GET", "/api/rag-knowledge/folders")).json(),
    enabled: open,
    staleTime: 0,
  });
  const kbFolders = foldersWithDocuments(allKbFolders, kbFolder);

  // The chosen voice must exist for the chosen language; otherwise take the first one listed.
  useEffect(() => {
    if (voices.length && !voices.some((v) => v.voice_id === voice)) setVoice(voices[0].voice_id);
  }, [voices, voice]);

  function stopPreview() {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(null);
  }

  function togglePreview(v: NovaVoice) {
    if (playing === v.voice_id) return stopPreview();
    stopPreview();
    const audio = new Audio(v.preview_url);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => {
      setPlaying(null);
      toast({ title: "Preview unavailable", description: "The sample for this voice could not be played.", variant: "destructive" });
    };
    audioRef.current = audio;
    setPlaying(v.voice_id);
    void audio.play().catch(() => setPlaying(null));
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        flowId: flowId === NO_FLOW ? "" : flowId,
        systemPrompt: instructions.trim(),
        language,
        voice,
        ...(phoneNumberId ? { phoneNumberId } : {}),
        // Same settings the ElevenLabs form saves: knowledge base, live agent transfer and meeting booking.
        // No host name or email: Cal.com sends the confirmation and calendar invite itself.
        knowledgeBaseFolderPath: kbFolder,
        transferPhoneNumber: transferNumber.trim(),
        meetingBookingEnabled: bookingEnabled,
        announceAi,
        meetingBookingUrl: bookingUrl.trim(),
        salesAgentEmails: attendees.map((e) => e.trim()).filter(Boolean),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      const res = editing
        ? await apiRequest("PATCH", `/api/agents/${agent!.id}/nova-sonic`, payload)
        : await apiRequest("POST", "/api/agents/nova-sonic", payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: editing ? "Agent updated" : "Agent created", description: `${name.trim()} is ready to make calls.` });
      queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/phone-numbers"] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ title: editing ? "Could not update the agent" : "Could not create the agent", description: error.message, variant: "destructive" });
    },
  });

  const selectedVoice = voices.find((v) => v.voice_id === voice);
  const needsContent = flowId === NO_FLOW && !instructions.trim();
  const needsNumber = !editing && !phoneNumberId;
  // The knowledge base is mandatory: the agent answers questions from its documents
  const canSave = name.trim().length > 0 && !needsContent && !needsNumber && !!voice && !!kbFolder && !save.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AudioLines className="h-5 w-5" /> {editing ? "Edit VoicePro Outbound Agent" : "Create VoicePro Outbound Agent"}
          </DialogTitle>
          <DialogDescription>
            Calls people from one of your phone numbers, follows your flow, and can book meetings on your Cal.com calendar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nova-agent-name">Agent name</Label>
            <Input id="nova-agent-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="B2B outbound pitch" data-testid="input-nova-agent-name" />
          </div>

          <div className="space-y-2">
            <Label>Flow</Label>
            <Select value={flowId} onValueChange={setFlowId}>
              <SelectTrigger data-testid="select-nova-flow">
                <SelectValue placeholder="Choose a flow" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_FLOW}>No flow: use the instructions below</SelectItem>
                {flows.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The agent follows the flow you built in the flow builder. Message, question, form, appointment, callback, branch, transfer, outcome, voicemail, do-not-call and end steps are supported; webhook steps are skipped.
            </p>
          </div>

          {flowId === NO_FLOW && (
            <div className="space-y-2">
              <Label htmlFor="nova-agent-instructions">Instructions</Label>
              <Textarea id="nova-agent-instructions" rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Who the agent is, what it should achieve on the call, and what it must never say." data-testid="input-nova-instructions" />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="select-nova-language">
                  <SelectValue placeholder="Loading languages..." />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {l.name} ({l.savedCount !== undefined ? `${l.savedCount} saved` : `${l.voiceCount} voices`})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {languageInfo && (
                <p className="text-xs text-muted-foreground">
                  {languageInfo.india
                    ? "Indian languages are listed first for your billing country."
                    : "English is listed first. Hindi and Indian English are for Indian billing countries."}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Phone number</Label>
              <Select value={phoneNumberId} onValueChange={setPhoneNumberId} disabled={editing}>
                <SelectTrigger data-testid="select-nova-number">
                  <SelectValue placeholder={numbersLoading ? "Loading numbers..." : plivoNumbers.length ? "Choose a number" : "No free phone numbers"} />
                </SelectTrigger>
                <SelectContent>
                  {plivoNumbers.map((n) => (
                    <SelectItem key={n.id} value={String(n.id)}>
                      {n.friendlyName || n.phoneNumber}
                      {n.provider ? ` (${n.provider === "twilio" ? "Twilio" : "Plivo"})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editing && <p className="text-xs text-muted-foreground">The number is fixed once the agent exists.</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Voice <span className="text-red-500">*</span></Label>
            <div className="flex gap-2">
              <Select value={voice} onValueChange={setVoice} disabled={voices.length === 0}>
                <SelectTrigger className="flex-1" data-testid="select-nova-voice">
                  <SelectValue placeholder={loadingVoices ? "Loading voices..." : "Choose a voice"} />
                </SelectTrigger>
                <SelectContent>
                  {voices.map((v) => (
                    <SelectItem key={`${v.voice_id}-${v.locale}`} value={v.voice_id}>
                      {v.name} ({v.labels.gender}, {v.locale})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={!selectedVoice}
                aria-label={selectedVoice && playing === selectedVoice.voice_id ? "Stop the voice sample" : "Play the voice sample"}
                onClick={() => selectedVoice && togglePreview(selectedVoice)}
                data-testid="button-nova-voice-sample"
              >
                {selectedVoice && playing === selectedVoice.voice_id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
            </div>
            {selectedVoice && <p className="text-xs text-muted-foreground">{selectedVoice.description}</p>}
            {loadingVoices && <p className="text-sm text-muted-foreground">Loading voices...</p>}
            {!loadingVoices && language && voices.length === 0 && (
              <div className="rounded-lg border border-dashed p-4 text-sm space-y-2" data-testid="nova-no-saved-voices">
                <p className="font-medium">No voices in My Voices for {languages.find((l) => l.code === language)?.name ?? "this language"} yet.</p>
                <p className="text-muted-foreground">Open the Voice Library, test a voice and press + Add. Then come back and refresh this list.</p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline">
                    <a href={withBase("/app/voices")} target="_blank" rel="noreferrer">Open Voice Library</a>
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => void refetchVoices()} disabled={fetchingVoices}>
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${fetchingVoices ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Only voices you added in My Voices are listed. These are fixed voices; voice cloning and pitch or accent changes are not available.
            </p>
          </div>

          {/* Knowledge base */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Knowledge base <span className="text-red-500">*</span>
            </Label>
            {foldersLoading ? (
              <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30 text-sm text-muted-foreground">Loading folders...</div>
            ) : kbFolders.length === 0 ? (
              <div className="border rounded-md p-3 bg-muted/30 text-sm text-center text-muted-foreground">
                No folders with documents found. Add documents to a folder in your Knowledge Base first.
              </div>
            ) : (
              <Select value={kbFolder} onValueChange={setKbFolder}>
                <SelectTrigger data-testid="select-nova-kb-folder" className={!kbFolder ? "border-orange-300" : ""}>
                  <SelectValue placeholder="Choose a folder..." />
                </SelectTrigger>
                <SelectContent>
                  {kbFolders.map((folder: any) => (
                    <SelectItem key={folder.path} value={folder.path}>
                      {folder.path === "/" ? "Root" : String(folder.path).replace(/^\/+/, "")} ({folder.count} {folder.count === 1 ? "doc" : "docs"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-muted-foreground">Required. Select a folder: the agent answers factual questions from the documents in it, and says so when it does not know.</p>
          </div>

          {/* Live agent transfer */}
          <div className="space-y-2">
            <Label htmlFor="nova-transfer" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Live agent transfer number <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input id="nova-transfer" type="tel" placeholder="+91 98450 00000" value={transferNumber} onChange={(e) => setTransferNumber(e.target.value)} data-testid="input-nova-transfer" />
            <p className="text-xs text-muted-foreground">
              When the caller asks for a person, the call is handed to this number. Flow steps that transfer use it too (the {"{{transfer_number}}"} placeholder).
            </p>
          </div>

          {/* Opening disclosure */}
          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-sm font-medium">Say it is an AI assistant when the call starts</Label>
                <p className="text-xs text-muted-foreground">
                  On: the agent names itself as an AI assistant in its first sentence. Off: it opens with the first line of the flow only. Either way it never claims to be human and says plainly that it is an AI if the caller asks. Check the calling rules for your country before turning this off.
                </p>
              </div>
              <Switch checked={announceAi} onCheckedChange={setAnnounceAi} data-testid="switch-nova-announce-ai" />
            </div>
          </div>

          {/* Meeting booking */}
          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-sm font-medium">Meeting booking with Cal.com</Label>
                <p className="text-xs text-muted-foreground">
                  The agent reads your Cal.com calendar and offers the caller two times, starting tomorrow within the next 5 business days. If the caller wants other times it offers two more, and keeps going until one suits. It then books the meeting.
                </p>
              </div>
              <Switch checked={bookingEnabled} onCheckedChange={setBookingEnabled} data-testid="switch-nova-booking" />
            </div>

            {bookingEnabled && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nova-booking-url">Cal.com link</Label>
                  {linkOptions.length > 0 && (
                    <Select
                      value={pickedLink}
                      onValueChange={(v) => {
                        if (v === LINK_DEFAULT) { setBookingUrl(""); setCustomLink(false); }
                        else if (v === LINK_CUSTOM) { setBookingUrl(""); setCustomLink(true); }
                        else { setBookingUrl(v); setCustomLink(false); }
                      }}
                    >
                      <SelectTrigger data-testid="select-nova-booking-link"><SelectValue placeholder="Choose a booking link" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={LINK_DEFAULT}>Account default</SelectItem>
                        {linkOptions.map((t) => (
                          <SelectItem key={t.id} value={t.link}>{t.title} ({t.duration} min) - {t.link.replace("https://", "")}</SelectItem>
                        ))}
                        <SelectItem value={LINK_CUSTOM}>Another Cal.com link...</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {showLinkInput && (
                  <Input
                    id="nova-booking-url"
                    type="url"
                    value={bookingUrl}
                    onChange={(e) => setBookingUrl(e.target.value)}
                    placeholder="https://cal.com/your-name/30min"
                    className={bookingUrl.trim() && !/^https:[/][/]([a-z0-9-]+[.])*cal[.](com|eu)[/][^/]+/i.test(bookingUrl.trim()) ? "border-orange-300" : ""}
                    data-testid="input-nova-booking-url"
                  />
                  )}
                  <p className="text-xs text-muted-foreground">
                    The Cal.com booking link whose calendar the agent reads and books on. Cal.com sends the confirmation and the calendar invite itself, to the caller and to the calendar owner, so no host name or email is needed. Leave empty to use the Cal.com event type set up for your account.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Additional meeting attendees <span className="text-muted-foreground text-xs">(optional)</span>
                  </Label>
                  {attendees.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setAttendees(attendees.map((a, i) => (i === index ? e.target.value : a)))}
                        placeholder="attendee@example.com"
                        className="flex-1"
                        data-testid={`input-nova-attendee-${index}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={attendees.length <= 1}
                        aria-label="Remove attendee"
                        onClick={() => setAttendees(attendees.filter((_, i) => i !== index))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setAttendees([...attendees, ""])}>
                    <Mail className="h-4 w-4 mr-2" />
                    Add attendee email
                  </Button>
                </div>

              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave} data-testid="button-save-nova-agent">
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editing ? "Save changes" : "Create agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
